// src/components/organisms/language-selector.tsx
// Seletor de idioma reutilizável (bandeira + dropdown pt-BR / en-US / es-ES).
// Extraído da GamifikTopBar para ser compartilhado entre o Onboarding A
// (top bar global) e o Onboarding B (shell próprio).
import { useRef, useState } from "react";

import { ChevronDown } from "lucide-react";
import { createPortal } from "react-dom";

import { useDropdownPlacement } from "@/hooks/use-dropdown-placement";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/utils";

import type { SupportedLocale } from "@/i18n/types";

function FlagBR() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 14" className="h-3.5 w-5 rounded-[2px]">
      <rect width="20" height="14" fill="#009C3B" />
      <polygon points="10,1.5 18.5,7 10,12.5 1.5,7" fill="#FFDF00" />
      <circle cx="10" cy="7" r="3.2" fill="#002776" />
      <path d="M7,6.4 Q10,5 13,6.4" stroke="#fff" strokeWidth="0.8" fill="none" />
    </svg>
  );
}

function FlagUS() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 14" className="h-3.5 w-5 rounded-[2px]">
      {[0,1,2,3,4,5,6].map((i) => (
        <rect key={i} x="0" y={i * 2} width="20" height="2" fill={i % 2 === 0 ? "#B22234" : "#fff"} />
      ))}
      <rect x="0" y="0" width="8" height="7" fill="#3C3B6E" />
    </svg>
  );
}

function FlagES() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 14" className="h-3.5 w-5 rounded-[2px]">
      <rect width="20" height="14" fill="#AA151B" />
      <rect y="3.5" width="20" height="7" fill="#F1BF00" />
    </svg>
  );
}

const localeOptions: { code: SupportedLocale; label: string; Flag: () => JSX.Element }[] = [
  { code: "pt-BR", label: "Português", Flag: FlagBR },
  { code: "en-US", label: "English",   Flag: FlagUS },
  { code: "es-ES", label: "Español",   Flag: FlagES },
];

export function LanguageSelector() {
  const { locale, setLocale } = useI18n();
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const placement = useDropdownPlacement(showLangDropdown, triggerRef, { maxHeight: 160 });

  const currentLocale = localeOptions.find((l) => l.code === locale);

  return (
    <div className="relative" style={{ fontFamily: "var(--font-turmas-sora, 'Sora', sans-serif)" }}>
      <button
        ref={triggerRef}
        onClick={() => setShowLangDropdown(!showLangDropdown)}
        className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
      >
        {currentLocale && <currentLocale.Flag />}
        <span>{currentLocale?.label}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            showLangDropdown && "rotate-180"
          )}
        />
      </button>
      {showLangDropdown && placement && typeof document !== "undefined" && createPortal(
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowLangDropdown(false)} />
          <div
            className="fixed z-20 w-32 overflow-auto rounded-lg border border-border bg-card py-1 shadow-lg"
            style={{
              top: placement.top,
              bottom: placement.bottom,
              right: window.innerWidth - (placement.left + placement.width),
              maxHeight: placement.maxHeight,
            }}
          >
            {localeOptions.map((opt) => (
              <button
                key={opt.code}
                onClick={() => {
                  setLocale(opt.code);
                  setShowLangDropdown(false);
                }}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors",
                  locale === opt.code
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <opt.Flag />
                {opt.label}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
